import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Maximize2, Play, X } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { isVideoFile, mediaIdFromUrl, mediaSrc, parseVideoEntry, vimeoId, youtubeId } from "../lib/media";
import { findCase, nextOf } from "../lib/cases";
import { useProjectCases, useProjectLang } from "../lib/project-view";
import { track } from "../lib/analytics";
import { useSEO, useJsonLd, breadcrumbLd } from "../lib/seo";
import "../projects.css";

const FEATURED_PROJECTS = ["baki-evleri", "art-tower", "25-plaza", "zenith-plaza", "fexri-tower", "cahan-holding"];

const CASE_PRESENTATION = {
  "baki-evleri": {
    name: { az: "Bakı Evləri", en: "BAKI EVLƏRİ" },
    scope: { az: "Konsept · Marketinq · Satış", en: "Concept · Marketing · Sales" },
    subtitle: { az: "Konseptdən satışa qədər.", en: "From concept to sales." },
    summary: { az: "Layihənin konseptini, brend mövqeyini və marketinqdən satışa qədər iş prosesini qurduq.", en: "We built the project's concept, brand position and operating process from marketing through sales." },
    concept: { az: "11 bina. 11 fərqli xarakter. Vahid brend konsepti.", en: "11 buildings. 11 distinct characters. One brand concept." },
    results: [
      {
        value: "200×+",
        label: { az: "Marketinq investisiyası → satış dövriyyəsi", en: "Marketing investment → sales turnover" },
        note: {
          az: "Marketinq investisiyası ilə formalaşan satış dövriyyəsi arasında 200×-dən yüksək nisbət qeydə alındı. Başqa sözlə, marketinqə yönəldilən hər 1 AZN investisiyaya qarşı 200 AZN-dən çox satış dövriyyəsi formalaşdı.",
          en: "A ratio above 200× was recorded between marketing investment and the sales turnover it generated. In other words, every 1 AZN invested in marketing generated more than 200 AZN in sales turnover.",
        },
      },
    ],
  },
  "art-tower": {
    scope: { az: "Rəqəmsal marketinq", en: "Digital marketing" },
    results: [{ value: "≈60%", label: { az: "12 ayda satılan mənzillər", en: "Apartments sold in 12 months" } }],
  },
  "25-plaza": {
    scope: { az: "Marketinq · İcarə", en: "Marketing · Leasing" },
    results: [{ value: "≈70%", label: { az: "4 ayda doluluq", en: "Occupancy in 4 months" } }],
  },
  "zenith-plaza": {
    scope: { az: "Mövqeləndirmə · İcarə", en: "Positioning · Leasing" },
    results: [
      { value: "≈40–50%", label: { az: "Təxminən 10 ayda doluluq", en: "Occupancy in approximately 10 months" } },
      { value: "7–8", label: { az: "Təxminən 7–8 ayda icarəyə verilən ofislər", en: "Offices leased in approximately 7–8 months" } },
    ],
  },
  "fexri-tower": {
    scope: { az: "Marketinq · Kreativ kommunikasiya", en: "Marketing · Creative communication" },
    results: [{ value: "70 sot", label: { az: "Həyət sahəsi · layihə faktı", en: "Courtyard area · project fact" } }],
  },
  "cahan-holding": {
    scope: { az: "Brend strategiyası · Portfel strukturu · Qablaşdırma istiqaməti · Kreativ istiqamət", en: "Brand strategy · Portfolio structure · Packaging direction · Creative direction" },
    results: [{ value: { az: "Açıqlanmır", en: "Not disclosed" }, label: { az: "Kommersiya göstəricisi dərc edilməyib", en: "Commercial figure not published" } }],
  },
};

const localized = (value, az) => value && typeof value === "object" ? value[az ? "az" : "en"] : value;
const metricParts = (value) => {
  const match = String(value).match(/^(.*?)(?:\s+)(AZN|sot)$/i);
  return match ? { value: match[1], unit: match[2] } : { value, unit: "" };
};

const GALLERY_DIMENSIONS = {
  "/cases/selected/baki-evleri/06.webp": [1536, 1024],
  "/cases/cahan-holding/01.jpg": [1536, 1536],
  "/cases/cahan-holding/02.png": [1448, 1086],
  "/cases/cahan-holding/03.jpg": [1320, 1575],
  "/cases/cahan-holding/04.jpg": [1320, 1594],
  "/cases/cahan-holding/05.png": [1448, 1086],
};

function galleryImage(item) {
  const src = typeof item === "string" ? item : item?.url || item?.src || "";
  const dimensions = GALLERY_DIMENSIONS[src] || (src.startsWith("/cases/selected/") ? [1122, 1402] : []);
  return { src, width: item?.width || dimensions[0], height: item?.height || dimensions[1] };
}

function Gallery({ images, client, az }) {
  const [active,setActive] = useState(0);
  const [open,setOpen] = useState(false);
  const move = (step) => setActive(i => (i + step + images.length) % images.length);
  return <Dialog.Root open={open} onOpenChange={setOpen}>
    <div className="case-edition-gallery">{images.map((image,i) => <Dialog.Trigger asChild key={`${image.src}-${i}`}><button type="button" className="case-edition-image" style={image.width && image.height ? { aspectRatio: `${image.width} / ${image.height}` } : undefined} data-testid={`gallery-image-${i}`} onClick={() => setActive(i)} aria-label={`${az ? "Şəkli aç" : "Open image"} ${i+1}`}><img src={image.src} width={image.width || undefined} height={image.height || undefined} alt={`${client} — ${az ? "kreativ iş" : "creative work"} ${i+1}`} loading="lazy" decoding="async"/><span><Maximize2 size={17}/></span></button></Dialog.Trigger>)}</div>
    <Dialog.Portal><Dialog.Overlay className="case-edition-dialog-overlay"/><Dialog.Content className="case-edition-lightbox" data-lenis-prevent aria-describedby={undefined} onKeyDown={e => {if(e.key === "ArrowRight") {e.preventDefault();move(1);} if(e.key === "ArrowLeft") {e.preventDefault();move(-1);}}}>
      <div className="case-edition-dialog-top"><Dialog.Title>{client}</Dialog.Title><Dialog.Close className="case-edition-icon-button" data-testid="gallery-close" aria-label={az ? "Bağla" : "Close"}><X/></Dialog.Close></div>
      <div className="case-edition-lightbox-stage"><img src={images[active].src} alt={`${client} — ${active+1}`}/></div>
      <div className="case-edition-dialog-controls"><button type="button" className="case-edition-icon-button" data-testid="gallery-prev" aria-label={az ? "Əvvəlki şəkil" : "Previous image"} onClick={() => move(-1)} disabled={images.length < 2}><ChevronLeft/></button><span aria-live="polite">{String(active+1).padStart(2,"0")} / {String(images.length).padStart(2,"0")}</span><button type="button" className="case-edition-icon-button" data-testid="gallery-next" aria-label={az ? "Növbəti şəkil" : "Next image"} onClick={() => move(1)} disabled={images.length < 2}><ChevronRight/></button></div>
    </Dialog.Content></Dialog.Portal>
  </Dialog.Root>;
}

function VideoLightbox({ entry, title, fallbackPoster, az, index }) {
  const [open, setOpen] = useState(false);
  const { url, thumbnail } = parseVideoEntry(entry);
  const resolved = mediaSrc(url);
  const mediaId = mediaIdFromUrl(url);
  const youtube = youtubeId(url);
  const vimeo = vimeoId(url);
  const poster = mediaSrc(thumbnail || (youtube ? `https://i.ytimg.com/vi/${youtube}/hqdefault.jpg` : mediaId ? `/api/media/${mediaId}/poster` : fallbackPoster));
  const playable = mediaId ? mediaSrc(`/api/media/${mediaId}/playable`) : resolved;
  const isEmbed = !!(youtube || vimeo);

  return <Dialog.Root open={open} onOpenChange={setOpen}>
    <Dialog.Trigger asChild>
      <button type="button" className="case-edition-video-trigger" data-testid={`project-video-trigger-${index}`} aria-label={`${az ? "Videoya bax" : "Play video"}: ${title}`}>
        {poster && <img src={poster} alt="" loading="lazy" decoding="async" />}
        <span className="case-edition-play"><Play size={19} fill="currentColor" aria-hidden="true" /></span>
        <span className="case-edition-video-label">{title}</span>
      </button>
    </Dialog.Trigger>
    <Dialog.Portal>
      <Dialog.Overlay className="case-edition-dialog-overlay" />
      <Dialog.Content className="case-edition-video-dialog" aria-describedby={undefined}>
        <div className="case-edition-dialog-top"><Dialog.Title>{title}</Dialog.Title><Dialog.Close className="case-edition-icon-button" aria-label={az ? "Bağla" : "Close"}><X /></Dialog.Close></div>
        <div className="case-edition-video-stage">
          {youtube ? <iframe src={`https://www.youtube-nocookie.com/embed/${youtube}?autoplay=1&rel=0`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
            : vimeo ? <iframe src={`https://player.vimeo.com/video/${vimeo}?autoplay=1&dnt=1`} title={title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
              : <video src={playable} poster={poster || undefined} controls autoPlay playsInline />}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}

export default function CaseStudy() {
  const {slug} = useParams();
  const {t,href,lang} = useProjectLang();
  const az = lang === "az";
  const cases = useProjectCases();
  const c = findCase(cases,slug);
  const orderedProjects = FEATURED_PROJECTS.map((projectSlug) => cases.find((project) => project.slug === projectSlug)).filter(Boolean);
  const related = orderedProjects.length ? [...orderedProjects, ...cases.filter((project) => project.id && !FEATURED_PROJECTS.includes(project.slug))] : cases;
  const next = c ? nextOf(related,slug) : null;
  const presentation = CASE_PRESENTATION[slug] || {};
  useEffect(() => {
    document.body.classList.add("project-route", "case-detail-route");
    return () => document.body.classList.remove("project-route", "case-detail-route");
  }, []);
  useEffect(() => {if(c) track("case_study_view",{case_slug:c.slug,case_client:c.client});},[c]);
  useSEO(c ? `${c.client} — YUXEL` : t("Work — YUXEL"),c ? t(c.summary) : "YUXEL",{image:c?.cover?.startsWith("/") ? c.cover : undefined,type:"article"});
  useJsonLd("breadcrumb",c && breadcrumbLd([{name:"YUXEL",path:href("/")},{name:t("Selected Work"),path:href("/work")},{name:c.client,path:href(`/work/${c.slug}`)}]));
  if(!c) return <div className="project-empty project-not-found"><h1>{t("Case study not found")}</h1><Link to={href("/work")} data-testid="case-detail-notfound-link">{t("View all work")}</Link></div>;
  const selectedGallery = slug === "baki-evleri" ? [] : (Array.isArray(c.gallery) ? c.gallery : []);
  const images = [...new Map(selectedGallery.map(galleryImage).filter((image) => image.src).map((image) => [image.src, image])).values()];
  const videos = slug === "baki-evleri" ? [
    "https://youtu.be/Gm8BcRexbF0", "https://youtu.be/wFKpze0nFg4", "https://youtu.be/coPTL-UOCBk",
    "https://youtu.be/1XCqvBuBOVI", "https://youtu.be/T3elVHNdEGQ", "https://youtu.be/TJW0IISFvo8", "https://youtu.be/brefa7JMMLo"
  ] : (Array.isArray(c.videos) ? c.videos : []);
  const rawResults = (c.impact || []).filter(metric => metric.v).map(metric => ({value: t(metric.v), label: t(metric.l), note: ""}));
  const results = rawResults.length ? rawResults : [{ value: az ? "Açıqlanmır" : "Not disclosed", label: az ? "Kommersiya göstəricisi dərc edilməyib" : "Commercial figure not published" }];
  const displayName = localized(presentation.name, az) || c.client;
  const subtitle = localized(presentation.subtitle, az) || t(c.title);
  const summary = localized(presentation.summary, az) || t(c.summary);
  const concept = localized(presentation.concept, az) || t(c.creative || c.summary);
  const scope = localized(presentation.scope, az);
  const hasVisuals = images.length > 0 || videos.length > 0;
  return <article className="project-page cinematic-case-page" key={slug}>
    <section className="case-cinematic-hero">
      {c.cover && <img className="case-cinematic-hero-image" src={c.cover} alt={`${displayName} — ${az ? "layihə vizualı" : "project cover"}`} loading="eager" decoding="async" />}
      <div className="case-cinematic-hero-overlay" aria-hidden="true" />
      <div className="case-cinematic-hero-inner project-container">
        <Link className="case-cinematic-back" to={href("/work")} data-testid="case-detail-back-link"><ArrowLeft size={15} aria-hidden="true" />{az ? "Bütün layihələr" : "All projects"}</Link>
        <div className="case-cinematic-copy">
          <p className="case-cinematic-sector">{t(c.segment || c.sector)}</p>
          <h1>{displayName}</h1>
          <p className="case-cinematic-subtitle">{subtitle}</p>
          {scope && <p className="case-cinematic-scope">{scope}</p>}
        </div>

      </div>
    </section>
    <section className="case-editorial-section project-container case-summary-after-hero">
      <div className="case-editorial-label"><span>01</span><p>{az ? "STRATEJİ ROL" : "STRATEGIC ROLE"}</p></div>
      <div className="case-editorial-content">
        <h2>{summary}</h2>
        {scope && <p className="case-editorial-meta">{scope}</p>}
        {c.note && <p className="case-editorial-note">{t(c.note)}</p>}
      </div>
    </section>
    <section className="case-editorial-section project-container case-approach-section">
      <div className="case-editorial-label"><span>02</span><p>{az ? "YANAŞMA" : "APPROACH"}</p></div>
      <div className="case-editorial-content">
        <h2>{concept}</h2>
        {c.context && <p>{t(c.context)}</p>}
        {Array.isArray(c.approach) && c.approach.length > 0 && <ol className="yx-case-approach">{c.approach.map((step, index) => <li key={index}><h3>{t(step.t)}</h3><p>{t(step.d)}</p></li>)}</ol>}
      </div>
    </section>
    <section className="case-editorial-section project-container case-result-section">
      <div className="case-editorial-label"><span>03</span><p>{az ? "TƏSİR VƏ NƏTİCƏ" : "IMPACT & OUTCOME"}</p></div>
      <div className="case-editorial-content"><h2>{t(c.outcome || "")}</h2>
        <div className="yx-case-result-grid">{results.map((metric, index) => { const parts = metricParts(metric.value); return <div className="yx-case-result" data-testid={`case-impact-${index}`} key={`${metric.value}-${metric.label}`}><strong>{parts.value}{parts.unit && <small> {parts.unit}</small>}</strong><span>{metric.label}</span>{metric.note && <p>{metric.note}</p>}</div>; })}</div>
      </div>
    </section>
    {hasVisuals && <section className="case-edition-creative project-container case-selected-work">
      <div className="case-editorial-section case-selected-work-heading">
        <div className="case-editorial-label"><span>04</span><p>{az ? "SEÇİLMİŞ İŞLƏR" : "SELECTED WORK"}</p></div>
        <div className="case-editorial-content"><h2>{az ? "Strategiya və kommunikasiya istiqamətinin real tətbiqlərindən seçilmiş nümunələr." : "Selected applications of the strategy and communication direction."}</h2></div>
      </div>
      {images.length > 0 && <Gallery key={slug} images={images} client={displayName} az={az} />}
      {videos.length > 0 && <div className="case-edition-videos">{videos.map((entry, index) => <VideoLightbox key={`${slug}-video-${index}`} entry={entry} title={`${displayName} — ${az ? "Video" : "Video"} ${index + 1}`} fallbackPoster={c.cover} az={az} index={index} />)}</div>}
    </section>}
    {next && next.slug !== c.slug && <section className="case-edition-next project-container"><Link to={href(`/work/${next.slug}`)} data-testid="case-next-link"><img src={next.cover} alt="" loading="lazy"/><span><span className="project-eyebrow">{az ? "NÖVBƏTİ LAYİHƏ" : "NEXT PROJECT"}</span><strong>{next.slug === "baki-evleri" ? "Bakı Evləri" : next.client}</strong></span><ArrowRight size={24} aria-hidden="true"/></Link></section>}
  </article>;
}
